import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, X, Loader2, Send, ArrowRight, PenLine, ChevronDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const SESSION_KEY = 'proxy_chat_session';
const CONV_KEY = 'proxy_chat_conv';
const WHATSAPP = '221774771818';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const DEFAULT_WELCOME = "Bonjour 👋 Je suis l'assistant Proxy Emobility. Comment puis-je vous aider ?";
const DEFAULT_PROACTIVE = "\uD83D\uDC4B Bonjour ! Une question sur nos motos \u00E9lectriques en pr\u00E9vente ? Je suis l\u00E0 pour vous aider.";
const DISMISS_KEY = 'proxy_chat_dismissed';
const PROACTIVE_KEY = 'proxy_chat_proactive_shown';
const DEFAULT_NO_MATCH =
  "Je n'ai pas la réponse exacte à votre question 🙂 Laissez-moi vos coordonnées et un conseiller Proxy vous recontactera rapidement.";
const LEAVE_MSG_LABEL = 'Laisser un message';

interface Cta {
  label: string;
  url: string;
}
interface ChatMsg {
  role: 'user' | 'assistant';
  content: string;
  cta?: Cta;
}
interface Faq {
  id: string;
  question: string;
  keywords: string[];
  answer: string;
  cta_label: string | null;
  cta_url: string | null;
  display_order: number;
}

/** Persistent anonymous session token (used in AI mode) */
function getSessionToken(): string {
  let t = localStorage.getItem(SESSION_KEY);
  if (!t) {
    t = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, t);
  }
  return t;
}

/** Normalise : minuscules, sans accents, ponctuation -> espaces */
const norm = (s: string) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s+]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Trouve la meilleure FAQ par score de mots-clés (sans IA) */
function matchFaq(text: string, faqs: Faq[]): Faq | null {
  const t = ' ' + norm(text) + ' ';
  let best: Faq | null = null;
  let bestScore = 0;
  for (const f of faqs) {
    let score = 0;
    for (const kw of f.keywords || []) {
      const k = norm(kw);
      if (!k) continue;
      if (t.includes(' ' + k + ' ')) score += k.includes(' ') ? 3 : 2; // mot entier
      else if (k.length >= 4 && t.includes(k)) score += 1; // sous-chaîne
    }
    if (score > bestScore) {
      bestScore = score;
      best = f;
    }
  }
  return bestScore >= 2 ? best : null;
}

const wantsHuman = (text: string) => {
  const t = norm(text);
  return [
    'laisser un message',
    'parler a un conseiller',
    'parler a un humain',
    'parler a quelqu',
    'un humain',
    'un conseiller',
    'rappelez moi',
    'rappeler moi',
    'me rappeler',
    'etre rappele',
  ].some((p) => t.includes(p));
};

/** Rend le texte avec liens cliquables (URLs http) */
function renderWithLinks(text: string) {
  return text.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noreferrer" className="underline text-primary-600 break-all">
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

/** Bouton CTA sous une réponse (lien interne ou externe) */
function CtaButton({ cta }: { cta: Cta }) {
  const cls =
    'inline-flex items-center gap-1 mt-2 px-3 py-1.5 rounded-full bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-colors';
  if (cta.url.startsWith('/')) {
    return (
      <Link to={cta.url} className={cls}>
        {cta.label} <ArrowRight size={13} />
      </Link>
    );
  }
  return (
    <a href={cta.url} target="_blank" rel="noreferrer" className={cls}>
      {cta.label} <ArrowRight size={13} />
    </a>
  );
}

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [welcome, setWelcome] = useState(DEFAULT_WELCOME);
  const [botName, setBotName] = useState('Assistant Proxy');
  const [enabled, setEnabled] = useState(true);
  const [mode, setMode] = useState<'rules' | 'ai'>('rules');
  const [noMatchMsg, setNoMatchMsg] = useState(DEFAULT_NO_MATCH);
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [leadStep, setLeadStep] = useState<'' | 'name' | 'phone'>('');
  const [interacted, setInteracted] = useState(false);
  const [scrolledDown, setScrolledDown] = useState(false);
  const [proactiveEnabled, setProactiveEnabled] = useState(true);
  const [proactiveDelay, setProactiveDelay] = useState(10);
  const [proactiveText, setProactiveText] = useState(DEFAULT_PROACTIVE);
  const [showTeaser, setShowTeaser] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const leadRef = useRef<{ name: string; phone: string; question: string }>({ name: '', phone: '', question: '' });

  // --- Chargement config + FAQ ---
  useEffect(() => {
    if (!supabase) return;
    supabase
      .from('chat_settings')
      .select('welcome_message, is_enabled, bot_name, mode, no_match_message, proactive_enabled, proactive_delay, proactive_message')
      .eq('id', 1)
      .single()
      .then(({ data }) => {
        if (!data) return;
        if (data.welcome_message) setWelcome(data.welcome_message);
        if (data.bot_name) setBotName(data.bot_name);
        if (data.no_match_message) setNoMatchMsg(data.no_match_message);
        setEnabled(data.is_enabled !== false);
        setMode(data.mode === 'ai' ? 'ai' : 'rules');
        setProactiveEnabled(data.proactive_enabled !== false);
        if (typeof data.proactive_delay === 'number' && data.proactive_delay > 0) setProactiveDelay(data.proactive_delay);
        if (data.proactive_message) setProactiveText(data.proactive_message);
      });
    supabase
      .from('chat_faq')
      .select('id, question, keywords, answer, cta_label, cta_url, display_order')
      .eq('is_active', true)
      .order('display_order', { ascending: true })
      .then(({ data }) => {
        if (data) setFaqs(data as Faq[]);
      });
    const saved = localStorage.getItem(CONV_KEY);
    if (saved) setConversationId(saved);
  }, []);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, leadStep]);

  // Focus à l'ouverture
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 200);
  }, [isOpen]);

  // Bouton flottant visible après scroll
  useEffect(() => {
    const onScroll = () => setScrolledDown(window.scrollY > 240);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Message proactif automatique après délai
  useEffect(() => {
    if (!enabled || !proactiveEnabled || dismissed || isOpen || interacted) return;
    try { if (sessionStorage.getItem(PROACTIVE_KEY) === '1') return; } catch { /* */ }
    const id = window.setTimeout(() => {
      setShowTeaser(true);
      try { sessionStorage.setItem(PROACTIVE_KEY, '1'); } catch { /* */ }
    }, Math.max(1, proactiveDelay) * 1000);
    return () => window.clearTimeout(id);
  }, [enabled, proactiveEnabled, proactiveDelay, dismissed, isOpen, interacted]);

  // Masquer la bulle quand le chat s'ouvre
  useEffect(() => {
    if (isOpen) { setShowTeaser(false); setInteracted(true); }
  }, [isOpen]);

  const pushUser = (content: string) => setMessages((m) => [...m, { role: 'user', content }]);
  const pushBot = (content: string, cta?: Cta) => setMessages((m) => [...m, { role: 'assistant', content, cta }]);

  const openFromTeaser = () => {
    setShowTeaser(false);
    setInteracted(true);
    setMessages((m) => (m.length === 0 ? [{ role: 'assistant', content: proactiveText }] : m));
    setIsOpen(true);
  };
  const dismissChat = () => {
    setShowTeaser(false);
    setIsOpen(false);
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); sessionStorage.setItem(PROACTIVE_KEY, '1'); } catch { /* */ }
  };

  // --- Capture de lead (laisser un message) ---
  const beginLead = useCallback(
    (question: string, fromNoMatch: boolean) => {
      leadRef.current = { name: '', phone: '', question: question || '' };
      pushBot(fromNoMatch ? noMatchMsg : 'Avec plaisir 🙂 Laissez-moi vos coordonnées, un conseiller vous recontactera.');
      setLeadStep('name');
      setTimeout(() => pushBot('Pour commencer, quel est votre prénom ?'), 200);
    },
    [noMatchMsg]
  );

  const submitLead = useCallback(async (phone: string) => {
    leadRef.current.phone = phone;
    setLoading(true);
    try {
      if (!supabase) throw new Error('no supabase');
      await supabase.from('form_submissions').insert({
        form_type: 'chatbot',
        form_name: 'Chatbot',
        customer_name: leadRef.current.name || null,
        customer_email: null,
        customer_phone: phone || null,
        subject: 'Message via chatbot',
        message: leadRef.current.question ? `Question : ${leadRef.current.question}` : 'Demande via le chatbot',
        source_page: typeof window !== 'undefined' ? window.location.pathname : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        status: 'new',
        raw_data: { via: 'chatbot', name: leadRef.current.name, phone, question: leadRef.current.question },
      });
      pushBot(
        `Merci ${leadRef.current.name || ''} 🙏 Votre demande est bien enregistrée, un conseiller vous recontactera au ${phone}. Pour aller plus vite, écrivez-nous sur WhatsApp via le bouton ci-dessous.`
      );
    } catch {
      pushBot(
        `Un souci technique nous empêche d'enregistrer votre demande. Contactez-nous directement sur WhatsApp via le bouton ci-dessous.`
      );
    } finally {
      setLeadStep('');
      setLoading(false);
    }
  }, []);

  // --- Mode IA (Edge Function) ---
  const aiSend = useCallback(
    async (text: string) => {
      setLoading(true);
      try {
        const token = getSessionToken();
        const res = await fetch(`${SUPABASE_URL}/functions/v1/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            session_token: token,
            message: text,
            conversation_id: conversationId,
            page_context: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (data.conversation_id && data.conversation_id !== conversationId) {
          setConversationId(data.conversation_id);
          localStorage.setItem(CONV_KEY, data.conversation_id);
        }
        pushBot(data.reply || 'Désolé, une erreur est survenue.');
      } catch {
        pushBot(`Désolé, problème de connexion. Contactez-nous sur WhatsApp via le bouton ci-dessous.`);
      } finally {
        setLoading(false);
      }
    },
    [conversationId]
  );

  // --- Journalise une question sans réponse (best effort) ---
  const logUnanswered = (question: string) => {
    try {
      supabase?.from('chat_unanswered').insert({
        question,
        page_context: typeof window !== 'undefined' ? window.location.pathname : null,
      });
    } catch {
      /* ignore */
    }
  };

  // --- Routeur de message ---
  const handleSend = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text || loading) return;
      setInteracted(true);
      pushUser(text);
      setInput('');

      // Capture de lead en cours
      if (leadStep === 'name') {
        leadRef.current.name = text;
        setLeadStep('phone');
        setTimeout(() => pushBot('Merci ! Et votre numéro de téléphone (ou WhatsApp) ?'), 150);
        return;
      }
      if (leadStep === 'phone') {
        await submitLead(text);
        return;
      }

      // Demande explicite d'un humain
      if (wantsHuman(text)) {
        beginLead(text, false);
        return;
      }

      // Mode IA
      if (mode === 'ai') {
        await aiSend(text);
        return;
      }

      // Mode règles : matching FAQ
      const f = matchFaq(text, faqs);
      if (f) {
        const cta = f.cta_url ? { label: f.cta_label || 'En savoir plus', url: f.cta_url } : undefined;
        setTimeout(() => pushBot(f.answer, cta), 150);
        return;
      }

      // Aucune réponse -> on journalise + on propose de laisser un message
      logUnanswered(text);
      beginLead(text, true);
    },
    [loading, leadStep, mode, faqs, aiSend, beginLead, submitLead]
  );

  // Réponse directe garantie depuis une puce
  const answerFaq = (f: Faq) => {
    if (loading || leadStep) return;
    setInteracted(true);
    pushUser(f.question);
    const cta = f.cta_url ? { label: f.cta_label || 'En savoir plus', url: f.cta_url } : undefined;
    setTimeout(() => pushBot(f.answer, cta), 150);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  if (!enabled) return null;

  const quickReplies = faqs.slice(0, 6);
  const showChips = !leadStep && !loading && mode === 'rules' && quickReplies.length > 0;

  return (
    <>
      <button
        onClick={() => setIsOpen((o) => !o)}
        aria-label={isOpen ? 'Fermer le chat' : 'Ouvrir le chat'}
        className={`fixed bottom-20 right-4 md:bottom-6 md:right-6 z-[101] h-14 w-14 rounded-full shadow-lg items-center justify-center transition-all duration-300 ${
          isOpen
            ? 'hidden sm:flex bg-neutral-900'
            : `flex bg-primary-600 hover:bg-primary-700 hover:scale-105 ${scrolledDown ? 'opacity-100 translate-y-0' : 'pointer-events-none opacity-0 translate-y-4'}`
        }`}
        style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
      >
        {isOpen ? <X size={24} className="text-white" /> : <MessageCircle size={24} className="text-white" />}
        {!isOpen && !interacted && (
          <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 bg-amber-400 rounded-full ring-2 ring-white animate-pulse" />
        )}
      </button>

      {showTeaser && !isOpen && (
        <div className="fixed bottom-36 right-4 md:bottom-24 md:right-6 z-[99] w-[270px] max-w-[80vw]">
          <div className="relative rounded-2xl rounded-br-sm bg-white shadow-2xl ring-1 ring-neutral-200 p-3.5 pr-8">
            <button onClick={() => setShowTeaser(false)} aria-label="Fermer" className="absolute top-1.5 right-1.5 p-1 text-neutral-400 hover:text-neutral-700">
              <X size={14} />
            </button>
            <button onClick={openFromTeaser} className="flex items-start gap-2.5 text-left">
              <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100">
                <MessageCircle size={14} className="text-primary-700" />
              </span>
              <span className="text-sm leading-snug text-neutral-800">{proactiveText}</span>
            </button>
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className="fixed z-[100] bg-white shadow-2xl flex flex-col overflow-hidden inset-x-0 top-0 h-[100dvh] rounded-none border-0 sm:inset-x-auto sm:top-auto sm:bottom-24 sm:right-6 sm:w-[380px] sm:h-[560px] sm:max-h-[80vh] sm:rounded-2xl sm:border sm:border-neutral-200"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 flex items-center justify-between flex-shrink-0" style={{ paddingTop: 'max(0.875rem, env(safe-area-inset-top))', paddingBottom: '0.875rem' }}>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                <MessageCircle size={18} />
              </div>
              <div>
                <div className="font-bold text-sm leading-tight">{botName}</div>
                <div className="text-[11px] text-white/80 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-300" /> En ligne
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/15 active:bg-white/25 rounded-lg transition-colors" aria-label="Réduire le chat" title="Réduire">
                <ChevronDown size={22} />
              </button>
              <button onClick={dismissChat} className="p-2 hover:bg-white/15 active:bg-white/25 rounded-lg transition-colors" aria-label="Fermer le chat" title="Fermer">
                <X size={22} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-neutral-50">
            <div className="flex gap-2">
              <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <MessageCircle size={13} className="text-primary-700" />
              </div>
              <div className="bg-white rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm text-neutral-800 shadow-sm max-w-[85%] leading-relaxed">
                {welcome}
              </div>
            </div>

            {messages.map((m, i) =>
              m.role === 'user' ? (
                <div key={i} className="flex justify-end">
                  <div className="bg-primary-600 text-white rounded-2xl rounded-tr-sm px-3.5 py-2.5 text-sm shadow-sm max-w-[85%] leading-relaxed whitespace-pre-wrap">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={i} className="flex gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MessageCircle size={13} className="text-primary-700" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm text-neutral-800 shadow-sm max-w-[85%] leading-relaxed whitespace-pre-wrap">
                    {renderWithLinks(m.content)}
                    {m.cta && (
                      <div>
                        <CtaButton cta={m.cta} />
                      </div>
                    )}
                  </div>
                </div>
              )
            )}

            {loading && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={13} className="text-primary-700" />
                </div>
                <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                  <Loader2 size={16} className="animate-spin text-primary-600" />
                </div>
              </div>
            )}

            {/* Réponses rapides (puces) */}
            {showChips && (
              <div className="flex flex-wrap gap-2 pt-1 pl-9">
                {quickReplies.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => answerFaq(f)}
                    className="text-xs px-3 py-1.5 bg-white border border-neutral-200 rounded-full text-neutral-700 hover:border-primary-400 hover:text-primary-700 transition-colors"
                  >
                    {f.question}
                  </button>
                ))}
                <button
                  onClick={() => beginLead('', false)}
                  className="text-xs px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-full text-primary-700 hover:bg-primary-100 transition-colors inline-flex items-center gap-1"
                >
                  <PenLine size={12} /> {LEAVE_MSG_LABEL}
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-neutral-200 bg-white" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <a
              href={`https://wa.me/${WHATSAPP}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-1.5 py-2 text-[11px] text-neutral-500 hover:text-green-700 hover:bg-green-50 transition-colors border-b border-neutral-100"
            >
              <MessageCircle size={11} /> Parler à un conseiller sur WhatsApp
            </a>
            <div className="p-2.5 flex items-end gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={leadStep === 'phone' ? 'Votre numéro de téléphone…' : leadStep === 'name' ? 'Votre prénom…' : 'Écrivez votre message...'}
                disabled={loading}
                className="flex-1 px-3.5 py-2.5 bg-neutral-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:opacity-60"
              />
              <button
                onClick={() => handleSend(input)}
                disabled={loading || !input.trim()}
                aria-label="Envoyer"
                className="h-10 w-10 rounded-xl bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
